// combatpro/companions/PetsCompanionsModal.js
//
// Combat PRO - Players > Pets & Animal Companions (G268 Pet & Animal Companion Generator).
// A full modal opened from the Player section, never a permanent top-level page
// pill. It shows each player's saved animals (created in the Pet & Animal
// Companion Generator) across My Animals, Animal Profile, Growth & Development,
// Roles, Combat Readiness and History, and gives the DM a selection surface that
// lists every animal for the current players with its eligibility. Only animals
// the server admits can be added to combat. Training (G270) has its own nested
// surfaces here: overview, available, current, scheduled, progress, completed,
// commands and history as the Animal Training & Trainer Generator saved them, and
// Find Trainer / Trainer Details, where the player browses that saved shortlist
// and chooses a trainer (the server checks the choice; scheduling stays in the
// generator). Equipment, care and habitat are shown as the Animal Equipment, Care
// & Habitat Generator saved them. The DM's combat surface shows only each
// animal's training state; it never browses or books trainers.
// No optional chaining. No nullish coalescing. No em dash.

import React, { useContext, useEffect, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { loadOwnCompanions, loadCombatFeed, requestCombatEntry, selectTrainer } from './companionApi';
import { TRAINING_OWNER, MODAL_SECTIONS, G269_SECTIONS, G270_SECTIONS, sectionById, labelOf, ownerNames, groupByOwner, inCombat, reasonText, companionCombatant, companionPlayerProjection, recordedFor, equipmentView, trainingView, filterTrainers, trainingStateLine } from './companionEngine';
import { runtimeContextPayload } from '../integration/combatContextIdentity';
import { ensureSharingContext, shareInformationCard } from '../integration/sharingApi';

var CHOOSABLE_WAITING = ['fully_booked', 'requires_later_growth_stage'];

function Row(props) {
  if (props.value === null || props.value === undefined || props.value === '') { return null; }
  return <div className="vi-pac-row"><span>{props.label}</span><strong>{props.value}</strong></div>;
}

function List(props) {
  var items = (props.items || []).filter(function (x) { return x !== null && x !== undefined && String(x).trim(); });
  if (!items.length) { return <div className="vi-pac-dim">{props.empty || 'None recorded'}</div>; }
  return <ul className="vi-pac-list">{items.map(function (x, i) { return <li key={i}>{String(x)}</li>; })}</ul>;
}

function Meter(props) {
  var v = Math.max(0, Math.min(100, Number(props.value) || 0));
  return <div className="vi-pac-meter"><span>{props.label}</span><div className="vi-pac-meter-bar"><i style={{ width: v + '%' }}></i></div><b>{v}</b></div>;
}

function PetsCompanionsModal(props) {
  const ctx = useContext(CombatContext);
  const players = (ctx && ctx.state && ctx.state.players) ? ctx.state.players : [];
  const [section, setSection] = useState('animals');
  const [own, setOwn] = useState({ loading: true, ok: false, records: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [feed, setFeed] = useState({ loading: false, ok: false, owners: [] });
  const [notice, setNotice] = useState('');
  const [trainerFilter, setTrainerFilter] = useState({ bookable: false, local: false, tier: '' });
  const [trainerSort, setTrainerSort] = useState('soonest');
  const [detailId, setDetailId] = useState(null);
  const [choices, setChoices] = useState({});
  const [choiceNotice, setChoiceNotice] = useState('');
  const [shareSelection, setShareSelection] = useState([]);
  const [shareNotice, setShareNotice] = useState('');

  useEffect(function () {
    if (!props.isOpen) { return undefined; }
    var live = true;
    setOwn({ loading: true, ok: false, records: [] });
    loadOwnCompanions().then(function (res) { if (live) { setOwn(Object.assign({ loading: false }, res)); } });
    return function () { live = false; };
  }, [props.isOpen]);

  var names = ownerNames(players);
  var namesKey = names.join('|');
  useEffect(function () {
    if (!props.isOpen || section !== 'select') { return undefined; }
    var live = true;
    setFeed({ loading: true, ok: false, owners: [] });
    loadCombatFeed(namesKey ? namesKey.split('|') : []).then(function (res) { if (live) { setFeed(Object.assign({ loading: false }, res)); } });
    return function () { live = false; };
  }, [props.isOpen, section, namesKey]);

  if (!props.isOpen) { return null; }

  var groups = groupByOwner(own.records, players);
  var selected = null;
  (own.records || []).forEach(function (e) { if (e.summary && e.summary.companion_id === selectedId) { selected = e; } });
  var meta = sectionById(section);
  var record = selected ? selected.record : null;
  var summary = selected ? selected.summary : null;
  var snapshot = selected ? selected.combat_snapshot : null;

  async function bringIn(row) {
    setNotice('');
    var res = await requestCombatEntry(row.companion_id, names);
    if (!res.ok) {
      setNotice(row.name + ' cannot enter combat: ' + (reasonText(res) || labelOf(res.error)) + '.');
      return;
    }
    var combatant = companionCombatant(res);
    var added = combatant ? ctx.addCompanionCombatant(combatant) : false;
    setNotice(added ? row.name + ' has joined the combat on ' + row.owner_character_name + "'s side." : row.name + ' is already in this combat.');
  }

  async function shareCompanion(mode) {
    setShareNotice('');
    try {
      var identity = await ensureSharingContext(runtimeContextPayload(ctx.state));
      var canonicalPlayers = identity.players || [];
      var recipients = [];
      var visibility = 'PARTY';
      if (mode === 'owner') {
        var owner = canonicalPlayers.filter(function (player) { return String(player.display_name).toLowerCase() === String(summary.owner_character_name).toLowerCase(); })[0];
        if (!owner) { setShareNotice('The owner is not in this Combat PRO party.'); return; }
        visibility = 'SINGLE_PLAYER'; recipients = [owner.player_id];
      } else if (mode === 'selected') {
        recipients = canonicalPlayers.filter(function (player) { return shareSelection.indexOf(String(player.runtime_ref)) >= 0; }).map(function (player) { return player.player_id; });
        if (!recipients.length) { setShareNotice('Choose at least one player.'); return; }
        visibility = 'SELECTED_PLAYERS';
      }
      await shareInformationCard({
        campaign_id: identity.campaign_id, title: summary.name + ' · Companion',
        card_type: 'COMPANION', source_kind: 'pet-animal-companion-generator',
        source_ref: summary.companion_id, granularity: 'WHOLE_CARD', visibility: visibility,
        recipient_player_ids: recipients, player_projection: companionPlayerProjection(summary),
        dm_projection: { dm_only_facts: (summary.reasons || []).map(function (reason) { return reason.label || labelOf(reason.code); }), companion_state: summary.operational_projection || {} }
      });
      setShareNotice('Player-safe companion information shared through Player View.');
    } catch (_error) { setShareNotice('Companion information was not shared.'); }
  }

  function animalSection() {
    if (own.loading) { return <div className="vi-pac-dim">Loading saved animals...</div>; }
    if (!own.ok) { return <div className="vi-pac-dim">Saved animals are unavailable right now ({labelOf(own.error)}).</div>; }
    if (!groups.length) { return <div className="vi-pac-empty">No saved animals yet. Create and save animals in the Pet & Animal Companion Generator and they appear here for each character.</div>; }
    return groups.map(function (g) {
      return (
        <div key={g.owner_character} className="vi-pac-owner">
          <div className="vi-pac-owner-head">{g.owner_character_name}{g.in_party ? <span className="vi-pac-tag">In this combat</span> : null}</div>
          <div className="vi-pac-cards">
            {g.animals.map(function (e) {
              var s = e.summary;
              return (
                <button key={s.companion_id} type="button" className={'vi-pac-card' + (s.companion_id === selectedId ? ' is-on' : '')} aria-pressed={s.companion_id === selectedId} onClick={function () { setSelectedId(s.companion_id); setSection('profile'); }}>
                  <strong>{s.name}</strong>
                  <span>{s.species} &middot; {s.growth_stage_label}</span>
                  <small>{labelOf(s.primary_role)} &middot; {s.eligible ? 'Combat eligible' : 'Not combat eligible'}</small>
                </button>
              );
            })}
          </div>
        </div>
      );
    });
  }

  function needAnimal() {
    return <div className="vi-pac-empty">Choose an animal in My Animals first.</div>;
  }

  function body() {
    if (section === 'animals') { return animalSection(); }
    if (section === 'select') { return selectSection(); }
    if (!selected) { return needAnimal(); }
    if (G269_SECTIONS.indexOf(section) >= 0) { return g269Section(); }
    if (G270_SECTIONS.indexOf(section) >= 0) { return trainingSection(); }
    if (!meta.available) {
      return (
        <div>
          <div className="vi-pac-boundary">{meta.label} is managed by the {meta.owner}. Nothing here changes it; this is what is currently recorded for {summary.name}.</div>
          <List items={recordedFor(section, record)} />
        </div>
      );
    }
    if (section === 'profile') {
      var bio = record.biology || {};
      var id = record.identity || {};
      var temp = record.temperament || {};
      return (
        <div className="vi-pac-grid2">
          <div>
            <Row label="Name" value={id.display_name + (id.nickname ? ' (' + id.nickname + ')' : '')} />
            <Row label="Species" value={bio.species_display + (bio.scientific_name ? ' (' + bio.scientific_name + ')' : '')} />
            <Row label="Kind" value={labelOf(id.species_ref ? id.species_ref.kind : '')} />
            <Row label="Owner" value={summary.owner_character_name} />
            <Row label="Sex" value={labelOf(bio.sex)} />
            <Row label="Age" value={summary.age} />
            <Row label="Stage" value={summary.growth_stage_label} />
            <Row label="Size" value={labelOf(bio.size_band)} />
            <Row label="Acquired" value={id.acquisition ? labelOf(id.acquisition.origin) + (id.acquisition.date ? ' on ' + id.acquisition.date : '') : ''} />
          </div>
          <div>
            <div className="vi-pac-sub">Temperament</div>
            <List items={temp.individual_traits} />
            <div className="vi-pac-sub">Bond, trust and loyalty</div>
            <Meter label="Bond" value={record.relationship ? record.relationship.bond : 0} />
            <Meter label="Trust" value={record.relationship ? record.relationship.trust : 0} />
            <Meter label="Loyalty" value={record.relationship ? record.relationship.loyalty : 0} />
          </div>
        </div>
      );
    }
    if (section === 'growth') {
      var b = record.biology || {};
      return (
        <div>
          <Row label="Age" value={summary.age} />
          <Row label="Current stage" value={summary.growth_stage_label} />
          <Row label="Next stage" value={b.expected_next_stage ? labelOf(b.expected_next_stage) + (typeof b.days_to_next_stage === 'number' ? ' in ' + b.days_to_next_stage + ' campaign days' : '') : 'None'} />
          <Row label="Size now" value={labelOf(b.size_band)} />
          <Row label="Lifecycle" value={labelOf(b.lifecycle_model)} />
          <div className="vi-pac-sub">Stage changes</div>
          <List items={((record.history || {}).growth_transitions || []).map(function (t) { return labelOf(t.from) + ' to ' + labelOf(t.to) + ' (' + t.date + ')'; })} empty="No stage changes recorded yet" />
          <div className="vi-pac-dim">Animals age on the campaign clock in the Pet & Animal Companion Generator.</div>
        </div>
      );
    }
    if (section === 'roles') {
      var role = record.role || {};
      return (
        <div>
          <Row label="Primary role" value={labelOf(role.primary_role)} />
          <div className="vi-pac-sub">Secondary roles</div>
          <List items={(role.secondary_roles || []).map(labelOf)} />
          <div className="vi-pac-sub">Future roles</div>
          <List items={(role.potential_roles || []).map(labelOf)} />
          <div className="vi-pac-sub">What is still needed</div>
          <List items={(role.role_blockers || []).map(function (bk) { return labelOf(bk.role) + ': ' + labelOf(bk.reason_code); })} empty="Nothing outstanding" />
        </div>
      );
    }
    if (section === 'combat') {
      return (
        <div>
          <Row label="Combat state" value={summary.combat_state_label} />
          <Row label="Can enter combat" value={summary.eligible ? 'Yes' : 'No'} />
          <Row label="Why not" value={summary.eligible ? '' : reasonText(summary)} />
          <Row label="Readiness" value={labelOf(summary.readiness)} />
          <Row label="Training state" value={trainingStateLine(summary.training_state)} />
          <Row label="Availability" value={summary.availability} />
          {snapshot && snapshot.combat ? (
            <div>
              <div className="vi-pac-sub">Combat snapshot</div>
              <Row label="Size and space" value={labelOf(snapshot.combat.combat_size) + ', ' + snapshot.combat.space_ft + ' ft'} />
              <Row label="Hit points (guidance)" value={snapshot.combat.durability ? snapshot.combat.durability.hit_points_guidance : ''} />
              <Row label="Armour class (guidance)" value={snapshot.combat.defences ? snapshot.combat.defences.armour_class_guidance : ''} />
              <List items={(snapshot.combat.attacks || []).map(function (a) { return a.label + ' ' + (a.damage_guidance || ''); })} />
            </div>
          ) : null}
        </div>
      );
    }
    if (section === 'share') {
      return <div>
        <div className="vi-pac-boundary">Share the bounded player-safe companion card through the existing Information system. Hidden history, DM notes, trainer administration and operational restrictions stay protected.</div>
        <Row label="Companion" value={summary.name + ' · ' + summary.species} />
        <div className="vi-pac-sub">Selected players</div>
        <div className="vi-pac-share-players">{names.map(function (name, index) {
          var player = players.filter(function (candidate) { return !candidate.companion; })[index];
          var ref = player ? String(player.id) : name;
          return <label key={ref}><input type="checkbox" checked={shareSelection.indexOf(ref) >= 0} onChange={function (event) { setShareSelection(function (current) { return event.target.checked ? current.concat([ref]) : current.filter(function (value) { return value !== ref; }); }); }} /> {name}</label>;
        })}</div>
        <div className="vi-pac-actions"><button type="button" className="vi-btn vi-btn-sm" onClick={function () { shareCompanion('owner'); }}>Share with owner only</button><button type="button" className="vi-btn vi-btn-sm" onClick={function () { shareCompanion('party'); }}>Share with party</button><button type="button" className="vi-btn vi-btn-sm vi-btn-primary" onClick={function () { shareCompanion('selected'); }}>Share with selected</button></div>
        {shareNotice ? <div className="vi-pac-notice" role="status">{shareNotice}</div> : null}
      </div>;
    }
    if (section === 'history') {
      var h = record.history || {};
      return (
        <div>
          <div className="vi-pac-sub">Important events</div>
          <List items={h.important_events} />
          <div className="vi-pac-sub">Ownership</div>
          <List items={h.ownership_history} />
          <div className="vi-pac-sub">Combat history</div>
          <List items={h.combat_history_refs} empty="No combat recorded yet" />
        </div>
      );
    }
    return null;
  }

  function gp(v) { var n = Number(v) || 0; return (n >= 100 ? Math.round(n) : n) + ' gp'; }

  // G269 surfaces: what the Animal Equipment, Care & Habitat Generator saved onto this animal (read-only here).
  function g269Section() {
    var v = equipmentView(record, selected.profile);
    var note = <div className="vi-pac-boundary">Equipment, care and habitat are managed in the {meta.owner || 'Animal Equipment, Care & Habitat Generator'} in the Desktop Control Centre. This shows what is saved on {summary.name}; Combat PRO only uses the bounded equipment effects.</div>;
    if (section === 'equipment') {
      return <div>{note}<Row label="Fitted" value={v.worn.map(function (w) { return w.label; }).join(', ') || 'Nothing fitted'} /><Row label="Stored" value={v.stored.map(function (s) { return s.label; }).join(', ') || 'Nothing stored'} /><Row label="Needs attention" value={v.fit.filter(function (f) { return f.resize_possible || f.replacement_required; }).map(function (f) { return f.label + ' (' + f.fit_label + ')'; }).join(', ')} /></div>;
    }
    if (section === 'outfit') {
      var o = v.outfit;
      if (!o) { return <div>{note}<div className="vi-pac-empty">No outfit has been prepared for this animal yet.</div></div>; }
      return <div>{note}<Row label="Prepared for" value={o.role_label + ', ' + labelOf(o.context) + ', ' + labelOf(o.environment)} /><Row label="Approximate cost of what it lacks" value={gp(o.approximate_cost_gp)} />
        <div className="vi-pac-sub">Recommended</div><List items={(o.items || []).map(function (i) { return i.label + ' (' + labelOf(i.status) + (i.owned ? ', owned' : '') + ')'; })} />
        <div className="vi-pac-sub">Warnings</div><List items={o.warnings} empty="No warnings" /></div>;
    }
    if (section === 'gear') {
      return <div>{note}<div className="vi-pac-sub">Fitted</div><List items={v.worn.map(function (w) { return w.label + ': ' + w.fit_label + ', ' + w.condition.toLowerCase() + ', ' + w.quality.toLowerCase() + (w.slot ? ' (' + w.slot + ')' : ''); })} empty="Nothing fitted" />
        <div className="vi-pac-sub">Stored and carried</div><List items={v.stored.map(function (s) { return s.label + ' (' + s.condition.toLowerCase() + ')'; })} empty="Nothing stored" /></div>;
    }
    if (section === 'fit') {
      return <div>{note}<List items={v.fit.map(function (f) { return f.label + ': ' + f.fit_label + '. ' + f.remaining + (f.next_stage ? '. ' + f.next_stage + ' in ' + f.next_stage_in_days + ' days: ' + f.next_stage_fit : '') + (f.replacement_required ? '. Replacement required.' : (f.resize_possible ? '. Can be resized.' : '')); })} empty="Nothing fitted" /></div>;
    }
    if (section === 'equipment_history') {
      return <div>{note}<List items={v.history} empty="No equipment history yet" /><div className="vi-pac-sub">Repairs, resizes and replacements</div><List items={v.repairs.concat(v.resizes, v.replacements).map(function (h) { return h.date + ': ' + h.label + (h.cost_gp ? ', ' + gp(h.cost_gp) : ''); })} /></div>;
    }
    if (section === 'care') {
      return <div>{note}<Row label="Care profile" value={v.care.profile} /><Row label="Feeding" value={v.care.feeding} /><Row label="Grooming" value={v.care.grooming} /><Row label="Exercise" value={v.care.exercise} /><Row label="Housing" value={v.care.housing} /><Row label="Care state" value={v.care.state} />
        {v.care_plan ? <Row label="Recurring cost" value={gp(v.care_plan.costs_gp ? v.care_plan.costs_gp.weekly : 0) + ' a week'} /> : null}</div>;
    }
    if (section === 'supplies') {
      var sp = v.supplies;
      if (!sp) { return <div>{note}<div className="vi-pac-empty">No supply plan saved yet.</div></div>; }
      return <div>{note}<List items={(sp.supplies || []).filter(function (s) { return s.per_week; }).map(function (s) { return s.item + ': ' + s.per_week + ' ' + s.unit + ' a week, ' + gp(s.cost_gp_week); })} /><Row label="Weekly total" value={gp(sp.weekly_cost_gp)} /></div>;
    }
    if (section === 'travel') {
      var tr = v.travel;
      if (!tr) { return <div>{note}<div className="vi-pac-empty">No journey planned yet.</div></div>; }
      return <div>{note}<Row label="Journey" value={tr.days + ' days, ' + tr.mode_label} /><Row label="Food to carry" value={tr.supplies.food_units_to_carry + ' units'} /><Row label="Transport" value={(tr.transport || []).map(function (x) { return x.label; }).join(', ') || 'None needed'} />
        <div className="vi-pac-sub">Packing checklist</div><List items={(tr.checklist || []).map(function (c) { return (c.packed ? '[x] ' : '[ ] ') + c.item; })} /></div>;
    }
    if (section === 'habitat') {
      var h = v.habitat;
      return <div>{note}<Row label="Habitat" value={h ? h.label + ' (' + h.kind + ', ' + labelOf(h.quality).toLowerCase() + ')' : labelOf(v.habitat_id)} /><Row label="Suitability" value={h ? labelOf(h.status) : ''} /><div className="vi-pac-sub">Changes needed</div><List items={h ? h.modifications : []} empty="None recorded" /></div>;
    }
    return null;
  }

  async function chooseTrainer(trainerId) {
    setChoiceNotice('');
    var cid = summary.companion_id;
    var res = await selectTrainer(cid, trainerId);
    if (!res.ok) {
      if (res.error === 'trainer_cannot_take_animal') { setChoiceNotice('That trainer cannot take ' + summary.name + ': ' + ((res.reasons || []).join(' ') || 'not available') + '.'); return; }
      setChoiceNotice(res.error === 'trainer_not_on_shortlist' ? 'That trainer is no longer on the saved shortlist. Find trainers again in the ' + TRAINING_OWNER + '.' : 'The choice could not be saved (' + labelOf(res.error) + ').');
      return;
    }
    var next = Object.assign({}, choices);
    next[cid] = res.trainer_choice || null;
    setChoices(next);
    setChoiceNotice(res.trainer_choice ? res.trainer_choice.name + ' is your chosen trainer for ' + (res.trainer_choice.programme_label || 'this training') + '. Open the Animal Training & Trainer Generator to schedule it.' : 'Trainer choice cleared.');
  }

  function canChoose(t) { return !!t.bookable || CHOOSABLE_WAITING.indexOf(t.availability_state) >= 0; }

  // G270 surfaces: what the Animal Training & Trainer Generator saved onto this animal, plus trainer browsing and
  // choice from the saved shortlist. Schedules, progress and completion are run in the generator.
  function trainingSection() {
    var v = trainingView(record, selected.profile, selected);
    var cid = summary.companion_id;
    var choice = Object.prototype.hasOwnProperty.call(choices, cid) ? choices[cid] : v.choice;
    var note = <div className="vi-pac-boundary">Training and trainers are managed by the {TRAINING_OWNER} in the Desktop Control Centre. This shows what is saved for {summary.name}{v.as_of ? ' as of ' + v.as_of : ''}; trainers found there can be compared and chosen here.</div>;
    var status = choiceNotice ? <div className="vi-pac-notice" role="status">{choiceNotice}</div> : null;
    var unmanaged = !v.managed ? <div className="vi-pac-dim">No training has been planned for {summary.name} in the {TRAINING_OWNER} yet.</div> : null;
    function entryLine(e) { return e.label + ': ' + (e.status_label || '') + (e.trainer ? ', ' + e.trainer : '') + (e.start_date ? ', from ' + e.start_date : '') + (e.expected_completion ? ' to ' + e.expected_completion : '') + (e.duration ? ' (' + e.duration + ')' : ''); }
    if (section === 'training' || section === 'training_overview') {
      var ready = v.roles.filter(function (r) { return r.now === 'eligible'; }).map(function (r) { return r.label; });
      return <div>{note}{unmanaged}
        <Row label="Training level" value={v.level} />
        <Row label="Completed programmes" value={String(v.completed.length)} />
        <Row label="In training" value={v.current.map(function (e) { return e.label + (typeof e.progress_percent === 'number' ? ' (' + e.progress_percent + '%)' : ''); }).join(', ') || 'Nothing in progress'} />
        <Row label="Scheduled" value={v.schedule.map(function (e) { return e.label; }).join(', ') || 'Nothing scheduled'} />
        <Row label="Combat training" value={trainingStateLine(v.state)} />
        <Row label="Chosen trainer" value={choice ? choice.name + (choice.programme_label ? ' for ' + choice.programme_label : '') : ''} />
        <Row label="Training costs paid" value={v.costs_paid_gp ? gp(v.costs_paid_gp) : ''} />
        <div className="vi-pac-sub">Roles it is ready for</div><List items={ready} empty="None yet" /></div>;
    }
    if (section === 'training_available') {
      if (!v.available.length) { return <div>{note}{unmanaged}<div className="vi-pac-empty">Available training is listed once {summary.name} has been opened in the {TRAINING_OWNER}.</div></div>; }
      return <div>{note}{v.available.filter(function (f) { return f.programmes.length; }).map(function (f) {
        return <div key={f.label}><div className="vi-pac-sub">{f.label} <small>{f.counts.completed} done, {f.counts.ready} ready</small></div>
          <List items={f.programmes.map(function (pr) { return pr.label + ': ' + labelOf(pr.status) + (pr.gate_label && pr.status !== 'ready' ? ' (' + pr.gate_label + ')' : '') + (pr.missing_prerequisites && pr.missing_prerequisites.length ? '. First: ' + pr.missing_prerequisites.join(', ') : '') + (pr.trainer_duration ? '. ' + pr.trainer_duration + ' with a competent trainer' : '') + (pr.self_allowed ? ', self-training possible' : ''); })} /></div>;
      })}</div>;
    }
    if (section === 'training_current') {
      return <div>{note}{unmanaged}<List items={v.current.map(function (e) { return entryLine(e) + (e.status_reason ? '. ' + e.status_reason : ''); })} empty="Nothing is in training right now" /></div>;
    }
    if (section === 'trainer') {
      if (!v.shortlist.length) { return <div>{note}{status}<div className="vi-pac-empty">No trainer shortlist saved yet. Use Find Trainer in the {TRAINING_OWNER} for {summary.name}; the trainers it finds appear here to compare and choose.</div></div>; }
      var shown = filterTrainers(v.shortlist, trainerFilter, trainerSort);
      return <div>{note}{status}
        <Row label="Trainers found for" value={v.shortlist_programme + (v.shortlist_place ? ' near a ' + v.shortlist_place.toLowerCase() : '')} />
        <div className="vi-pac-filters">
          <label><input type="checkbox" checked={trainerFilter.bookable} onChange={function (e) { setTrainerFilter(Object.assign({}, trainerFilter, { bookable: e.target.checked })); }} /> Can take this animal</label>
          <label><input type="checkbox" checked={trainerFilter.local} onChange={function (e) { setTrainerFilter(Object.assign({}, trainerFilter, { local: e.target.checked })); }} /> Local only</label>
          <label>Tier <select aria-label="Trainer tier" value={trainerFilter.tier} onChange={function (e) { setTrainerFilter(Object.assign({}, trainerFilter, { tier: e.target.value })); }}><option value="">Any</option>{['novice', 'competent', 'expert', 'master', 'legendary'].map(function (t) { return <option key={t} value={t}>{labelOf(t)}</option>; })}</select></label>
          <label>Sort <select aria-label="Sort trainers" value={trainerSort} onChange={function (e) { setTrainerSort(e.target.value); }}><option value="soonest">Soonest available</option><option value="reliability">Most reliable</option><option value="reputation">Best reputation</option><option value="tier">Highest tier</option></select></label>
        </div>
        {!shown.length ? <div className="vi-pac-dim">No saved trainer matches these filters.</div> : (
          <table className="vi-pac-table vi-pac-trainers">
            <thead><tr><th>Trainer</th><th>Tier</th><th>Availability</th><th>Next available</th><th>Reliability</th><th></th></tr></thead>
            <tbody>
              {shown.map(function (t) {
                var on = choice && choice.trainer_id === t.trainer_id;
                return <tr key={t.trainer_id} className={on ? 'is-chosen' : ''}>
                  <td><strong>{t.name}</strong><div className="vi-pac-dim">{t.archetype_label}</div></td>
                  <td>{t.tier_label}</td>
                  <td>{t.availability_label}</td>
                  <td>{t.next_available_date || 'Not available'}</td>
                  <td>{t.reliability_percent}%</td>
                  <td><button type="button" className="vi-btn vi-btn-sm" onClick={function () { setDetailId(t.trainer_id); setSection('trainer_details'); }}>Details</button> {on ? <span className="vi-pac-tag">Chosen</span> : (canChoose(t) ? <button type="button" className="vi-btn vi-btn-sm vi-btn-primary" onClick={function () { chooseTrainer(t.trainer_id); }}>Choose</button> : <span className="vi-pac-dim">Cannot take this animal</span>)}</td>
                </tr>;
              })}
            </tbody>
          </table>
        )}
      </div>;
    }
    if (section === 'trainer_details') {
      var wanted = detailId || (choice ? choice.trainer_id : '');
      var t = v.shortlist.filter(function (x) { return x.trainer_id === wanted; })[0] || v.shortlist[0];
      if (!t) { return <div>{note}<div className="vi-pac-empty">Choose a trainer in Find Trainer to see their details.</div></div>; }
      var picked = choice && choice.trainer_id === t.trainer_id;
      var spec = t.specialties || {};
      return <div>{note}{status}
        <div className="vi-pac-sub">{t.name}</div>
        <Row label="Kind" value={t.archetype_label} />
        <Row label="Tier and quality" value={t.tier_label + ', ' + labelOf(t.quality).toLowerCase()} />
        <Row label="Reliability" value={t.reliability_percent + '%'} />
        <Row label="Reputation" value={String(t.reputation)} />
        <Row label="Facility" value={t.facility} />
        <Row label="Location" value={t.location + (t.local ? ' (local)' : (t.travel_days ? ', ' + t.travel_days + ' days away' : ''))} />
        <Row label="Availability" value={t.availability_label} />
        <Row label="Next available" value={t.next_available_date || 'Not available'} />
        <div className="vi-pac-sub">Specialties</div><List items={(spec.capabilities || []).concat(spec.families ? ['Families: ' + spec.families.join(', ')] : [])} />
        <div className="vi-pac-sub">Why not</div><List items={t.availability_reasons} empty="Nothing stops this trainer taking the animal" />
        <div className="vi-pac-dim">{t.player_summary}</div>
        <div className="vi-pac-actions">
          {picked ? <button type="button" className="vi-btn vi-btn-sm" onClick={function () { chooseTrainer(null); }}>Clear choice</button> : (canChoose(t) ? <button type="button" className="vi-btn vi-btn-sm vi-btn-primary" onClick={function () { chooseTrainer(t.trainer_id); }}>Choose this trainer</button> : null)}
          <button type="button" className="vi-btn vi-btn-sm" onClick={function () { setSection('trainer'); }}>Back to trainers</button>
        </div>
      </div>;
    }
    if (section === 'training_schedule') {
      return <div>{note}{status}{unmanaged}
        <Row label="Chosen trainer" value={choice ? choice.name + ' (' + choice.tier_label + ')' + (choice.programme_label ? ' for ' + choice.programme_label : '') : 'No trainer chosen yet'} />
        <div className="vi-pac-sub">Scheduled training</div>
        <List items={v.schedule.map(entryLine)} empty="Nothing scheduled" />
        <div className="vi-pac-dim">Schedules are set on the campaign calendar in the {TRAINING_OWNER}; a trainer chosen here is waiting for {summary.name} there.</div></div>;
    }
    if (section === 'training_progress') {
      return <div>{note}{unmanaged}
        {v.current.length ? v.current.map(function (e, i) { return <div key={i}><Meter label={e.label} value={e.progress_percent} /><Row label="Status" value={e.status_label + (e.status_reason ? ': ' + e.status_reason : '')} /><Row label="Expected completion" value={e.expected_completion} /></div>; }) : <div className="vi-pac-dim">Nothing is in training right now.</div>}
        <div className="vi-pac-sub">Paused, failed or abandoned</div>
        <List items={v.ended.map(function (e) { return e.label + ': ' + e.status_label + (e.outcome ? ' (' + e.outcome + ')' : ''); })} empty="None" /></div>;
    }
    if (section === 'training_completed') {
      return <div>{note}<List items={v.completed.map(function (c) { return c.label + (c.family ? ' (' + c.family + ')' : '') + (c.date ? ', ' + c.date : '') + (c.trainer ? ', ' + c.trainer : ''); })} empty="No completed training yet" /></div>;
    }
    if (section === 'training_commands') {
      return <div>{note}
        {v.commands.map(function (f) { return <div key={f.label}><div className="vi-pac-sub">{f.label}</div><List items={f.skills} /></div>; })}
        <div className="vi-pac-sub">Custom commands</div>
        <List items={v.custom_commands.map(function (c) { return c.cue ? '"' + c.cue + '": ' + c.meaning : String(c); })} empty="No custom commands" />
        <div className="vi-pac-sub">Commands it does not know yet</div>
        <List items={v.limitations} empty="None" /></div>;
    }
    if (section === 'training_history') {
      return <div>{note}
        <div className="vi-pac-sub">Trainers</div><List items={v.trainer_history} empty="No trainers recorded yet" />
        <div className="vi-pac-sub">Training</div><List items={v.training_history} empty="No training recorded yet" /></div>;
    }
    return null;
  }

  function selectSection() {
    if (!names.length) { return <div className="vi-pac-empty">Add players to this combat first. Their characters' animals are listed here.</div>; }
    if (feed.loading) { return <div className="vi-pac-dim">Loading the party's animals...</div>; }
    if (!feed.ok) { return <div className="vi-pac-dim">The party's animals are unavailable right now ({labelOf(feed.error)}).</div>; }
    return (
      <div>
        <div className="vi-pac-boundary">Every animal owned by the characters in this combat, with its eligibility. Only eligible animals can be brought into combat. Each animal's training state is shown; training schedules and trainer choices stay outside combat.</div>
        {notice ? <div className="vi-pac-notice" role="status">{notice}</div> : null}
        {(feed.owners || []).map(function (g) {
          return (
            <div key={g.owner_character_name} className="vi-pac-owner">
              <div className="vi-pac-owner-head">{g.owner_character_name}</div>
              {!g.animals.length ? <div className="vi-pac-dim">No saved animals for this character.</div> : (
                <table className="vi-pac-table">
                  <thead><tr><th>Animal</th><th>Species</th><th>Age / stage</th><th>Roles</th><th>Combat</th><th>Training</th><th>Readiness</th><th></th></tr></thead>
                  <tbody>
                    {g.animals.map(function (row) {
                      var already = inCombat(players, row.companion_id);
                      return (
                        <tr key={row.companion_id} className={row.eligible ? 'is-eligible' : 'is-blocked'}>
                          <td><strong>{row.name}</strong></td>
                          <td>{row.species}</td>
                          <td>{row.age} &middot; {row.growth_stage_label}</td>
                          <td>{[labelOf(row.primary_role)].concat((row.secondary_roles || []).map(labelOf)).join(', ')}</td>
                          <td>{row.eligible ? 'Eligible' : 'Not eligible: ' + reasonText(row)}</td>
                          <td>{trainingStateLine(row.training_state)}</td>
                          <td>{labelOf(row.readiness)}</td>
                          <td>{row.eligible ? <button type="button" className="vi-btn vi-btn-sm vi-btn-primary" disabled={already} onClick={function () { bringIn(row); }}>{already ? 'In combat' : 'Add to combat'}</button> : <span className="vi-pac-dim">Cannot fight</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="vi-pac-modal" role="dialog" aria-modal="true" aria-label="Pets & Animal Companions">
      <div className="vi-pac-modal-card">
        <div className="vi-pac-modal-head">
          <div>
            <div className="vi-pac-eyebrow">Players</div>
            <div className="vi-pac-title">Pets &amp; Animal Companions</div>
            <div className="vi-pac-subtitle">{selected ? summary.name + ' · ' + summary.species + ' · ' + summary.owner_character_name : 'Every character’s animals, their growth, roles and combat readiness'}</div>
          </div>
          <button type="button" className="vi-pac-close" aria-label="Close" onClick={props.onClose}>&times;</button>
        </div>
        <div className="vi-pac-modal-main">
          <nav className="vi-pac-nav" aria-label="Pets & Animal Companions sections">
            {MODAL_SECTIONS.map(function (s) {
              return <button key={s.id} type="button" className={'vi-pac-navbtn' + (section === s.id ? ' is-on' : '') + (s.available ? '' : ' is-later') + (s.parent ? ' is-nested' : '')} aria-current={section === s.id ? 'true' : undefined} onClick={function () { setSection(s.id); }}>{s.label}</button>;
            })}
          </nav>
          <div className="vi-pac-body">{body()}</div>
        </div>
      </div>
      <div className="vi-pac-scrim" onClick={props.onClose}></div>
    </div>
  );
}

export default PetsCompanionsModal;
